import type { PublicReview } from '@xshop/shared';
import type { BoundValue, StoreFacts } from './storeFacts';

// What a review card's bound parts say: who wrote it, what they said, how many stars, and the product it is
// about. A Reviews list repeats its card once per approved review, each copy reading its own review here.

/** One review as a card's bindings read it. */
export type ReviewFacts = {
  author: string;
  body: string;
  title: string;
  rating: number;
  verified: boolean;
  /** The reviewer's own photo when they added one, else the product's. */
  image: string;
  productTitle: string;
  productHref: string;
  productImage: string;
  /** The reviewer's voice note or video, when the shop attached one; '' when none. */
  audio: string;
  video: string;
  /** The store's reply under the review; '' when it has not replied. */
  reply: string;
};

/** "Verified buyer", in each language a store may be served in. */
const VERIFIED: Readonly<Record<'ar' | 'fr' | 'en', string>> = { en: 'Verified buyer', fr: 'Acheteur vérifié', ar: 'مشتري موثّق' };

/** How each review source is answered from the review's facts. */
const REVIEW_FACTS: Readonly<Record<string, (review: ReviewFacts, facts: StoreFacts) => BoundValue>> = {
  'review.author': (review) => review.author,
  'review.body': (review) => review.body,
  'review.title': (review) => review.title,
  'review.rating': (review) => review.rating,
  'review.verified': (review, facts) => (review.verified ? VERIFIED[facts.language ?? 'en'] : ''),
  'review.image': (review) => review.image || undefined,
  'review.productTitle': (review) => review.productTitle,
  'review.productHref': (review) => review.productHref,
  'review.productImage': (review) => review.productImage || undefined
};

// Answers a review source, or nothing outside a review card.
export function reviewFact(source: string, facts: StoreFacts): BoundValue {
  return facts.review ? REVIEW_FACTS[source]?.(facts.review, facts) : undefined;
}

// Reads one approved review into what its card's bindings read.
export const reviewFactsOf = (row: PublicReview): ReviewFacts => ({
  author: row.customerName, body: row.body, title: row.title ?? '', rating: row.rating, verified: row.verifiedBuyer,
  image: row.images[0] ?? row.productImage ?? '', productTitle: row.productTitle,
  productHref: `/products/${encodeURIComponent(row.productSlug)}`, productImage: row.productImage ?? '',
  audio: row.audio[0] ?? '', video: row.videos[0] ?? '', reply: row.adminReply?.message.trim() ?? ''
});
